import CarouselCardSmall from 'components/OfferList/OfferCards/OfferCardSmall/CarouselCardSmall'
import type { ComponentProps } from 'react'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import TourV2Location from './TourV2Location'
import useCheapestOfTourV2Offer from 'hooks/TourV2/useCheapestOfTourV2Offer'
import useTourV2VariationDetails from 'hooks/TourV2/useTourV2VariationDetails'
import TourV2PriceDetails from './TourV2PriceDetails'
import TourV2UrgencyTags from 'components/Tours/TourV2UrgencyTags'
import TourTravelTagLabel from 'components/Tours/TourTravelTagLabel'
import TourCampaignTravelTagLabel from 'components/Tours/TourCampaignTravelTagLabel'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import CarouselCardMedium from './OfferCardMedium/CarouselCardMedium'
import { middleDotSeparatorMessage } from 'LuxKit/intl/separators'
import { isOfferRatingDisplayable } from 'lib/order/reviewUtils'
import { isTourProductTypeStyled } from 'lib/tours/tourUtils'
import findCheapestTourV2PurchasableOption from 'lib/tours/findCheapestTourV2PurchasableOption'
import { generateLoyaltyPointsCalculatorTourOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import LoyaltyPointsEarn from 'loyalty/components/Earn/LoyaltyPointsEarn'
import useTourStyledLabels from 'hooks/Tours/useTourStyledLabels'
import AgentHubEarnYourOwnTourLabel from 'agentHub/components/AgentHubEarnYourOwnTourLabel'

type InheritedCarouselCardMediumProps = Pick<ComponentProps<typeof CarouselCardMedium>, 'variant' | 'eagerLoadImage'>

interface Props extends InheritedCarouselCardMediumProps {
  offer: App.Tours.TourV2Offer | App.Tours.TourV2OfferSummary
  onClick: () => void
  className?: string
  offerUrl: string
  size: 'small' | 'medium'
}

function TourV2OfferCard(props: Props) {
  const intl = useIntl()
  const { styledProductTypeTitles } = useTourStyledLabels()
  const {
    offer,
    onClick,
    className,
    offerUrl,
    size,
    variant,
    eagerLoadImage,
  } = props
  const { cheapestPurchasableOption, cheapestVariation } = useCheapestOfTourV2Offer(offer)
  const variationDetails = useTourV2VariationDetails(cheapestVariation)

  const providerName = useMemo<string>(() => {
    const sep = intl.formatMessage(middleDotSeparatorMessage)
    return [
      offer.operatedBy,
      isTourProductTypeStyled(offer.productType) && offer.productType !== 'custom_tour' ? styledProductTypeTitles[offer.productType] : undefined,
    ].filter(Boolean).join(` ${sep} `)
  }, [offer, intl, styledProductTypeTitles])

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(
    () => {
      const cheapestPurchasableOption = findCheapestTourV2PurchasableOption(offer)
      const nights = variationDetails.daysCount && variationDetails.daysCount > 0 ? variationDetails.daysCount - 1 : undefined
      return [
        generateLoyaltyPointsCalculatorTourOptions(offer,
          {
            price: cheapestPurchasableOption?.price,
            memberPrice: cheapestPurchasableOption?.memberPrice,
            roomType: cheapestPurchasableOption?.roomType,
            packageId: cheapestPurchasableOption?.fkDepartureId,
            offerId: cheapestPurchasableOption?.fkTourId,
            nights,
          }),
      ]
    },
    [offer, variationDetails.daysCount],
  )

  if (!cheapestPurchasableOption || !cheapestVariation) {
    return null
  }

  const {
    startLocation,
    endLocation,
    hasTheSameStartAndEndLocation,
  } = variationDetails

  const updatedEndLocation = hasTheSameStartAndEndLocation ? startLocation : endLocation

  return (
    <ProductPaletteProvider product={offer}>
      {size === 'medium' && <CarouselCardMedium
        variant={variant}
        className={className}
        eagerLoadImage={eagerLoadImage}
        bookmarkButton={<BookmarkButton offer={offer} withLabel="tablet" />}
        title={offer.name}
        rating={isOfferRatingDisplayable(offer.rating, 0) ? offer.rating : undefined}
        providerName={providerName}
        image={cheapestVariation.images[0]}
        images={cheapestVariation.images}
        location={<TourV2Location startLocation={startLocation} endLocation={updatedEndLocation} variant="start_and_end" />}
        priceDetails={<TourV2PriceDetails offer={offer} variant="condensed" />}
        to={offerUrl}
        imageCornerLabel={<AgentHubEarnYourOwnTourLabel offerId={offer.id} />}
        urgencyLabels={<TourV2UrgencyTags
          offer={offer}
          suffixLabels={<>
            <TourTravelTagLabel tourId={offer.id} />
            <TourCampaignTravelTagLabel campaignTags={offer.campaignTags} />
          </>}
        />}
        onClick={onClick}
        loyaltyPointsElement={<LoyaltyPointsEarn
          calculationRequests={pointsEarnCalculationRequests}
          calculationType="estimate"
          offer={offer}
        />}
      />}
      {size === 'small' && <CarouselCardSmall
        variant={variant}
        className={className}
        eagerLoadImage={eagerLoadImage}
        rating={isOfferRatingDisplayable(offer.rating, 0) ? offer.rating : undefined}
        bookmarkButton={<BookmarkButton offer={offer} withLabel="tablet" />}
        image={cheapestVariation.images[0]}
        images={cheapestVariation.images}
        location={<TourV2Location startLocation={startLocation} endLocation={updatedEndLocation} variant="start_and_end" />}
        title={offer.name}
        priceDetails={<TourV2PriceDetails offer={offer} />}
        to={offerUrl}
        imageCornerLabel={<AgentHubEarnYourOwnTourLabel offerId={offer.id} />}
        urgencyLabels={<TourV2UrgencyTags
          offer={offer}
          suffixLabels={<>
            <TourTravelTagLabel tourId={offer.id} />
            <TourCampaignTravelTagLabel campaignTags={offer.campaignTags} />
          </>}
        />}
        onClick={onClick}
        loyaltyPointsElement={<LoyaltyPointsEarn
          calculationRequests={pointsEarnCalculationRequests}
          calculationType="estimate"
          offer={offer}
        />}
      />}
    </ProductPaletteProvider>
  )
}

export default TourV2OfferCard
